import React, { useRef } from 'react';
import { Formik } from 'formik';
import { Button, Col, Modal } from 'antd';
import { Form, Input } from 'formik-antd';
import * as Yup from 'yup';

import {
  TimesheetStatusEnum,
  TimesheetStatusReportDetailRequestModel,
} from 'api/backofficeApiClient';
import { ExtraItemEnum } from 'enums';
import FormItem from 'components/formik/FormItem';
import { getDateWithoutTime } from 'utils/dateUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import SubmitButton from 'components/formik/SubmitButton';
import { TimesheetStatusReportFilterTypes } from '../filters/enums';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { sendTimesheetStatusReportDetailsExcel } from 'redux/reducers/homeDashboardSlice';
import { TimesheetStatusReportFilters } from '../TimesheetStatusReportFilter';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  filters: TimesheetStatusReportFilters;
  onClose: () => void;
  pageNumber: number;
  pageSize: number;
  isAdmin?: boolean;
};

interface SendReportModalFormValues {
  email: string;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email('Email format is invalid')
    .required('Field is required')
    .max(120, 'Email should be 120 characters maximum'),
});

export default function SendReportModal(props: Props) {
  const { filters, onClose, pageNumber, pageSize, isAdmin } = props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();

  const initialValues: SendReportModalFormValues = {
    email: '',
  };

  const handleCloseModal = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current.dirty) {
      return Modal.confirm({
        title: 'Changes won’t be saved. Are you sure?',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const handleSubmit = async (values: SendReportModalFormValues) => {
    const { email } = values;

    const timesheetStatusReportDetailRequest: TimesheetStatusReportDetailRequestModel =
      {
        pageNumber,
        pageSize,
        states: !filters[
          TimesheetStatusReportFilterTypes.ApprovalState
        ].includes(TimesheetStatusEnum.All)
          ? filters[TimesheetStatusReportFilterTypes.ApprovalState]
          : [],
        customerIds: !filters[
          TimesheetStatusReportFilterTypes.Customer
        ].includes(ExtraItemEnum.All)
          ? filters[TimesheetStatusReportFilterTypes.Customer]
          : [],
        workerIds: !filters[TimesheetStatusReportFilterTypes.Worker].includes(
          ExtraItemEnum.All,
        )
          ? filters[TimesheetStatusReportFilterTypes.Worker]
          : [],
        startDate: filters[TimesheetStatusReportFilterTypes.Date]?.startDate
          ? getDateWithoutTime(
              filters[TimesheetStatusReportFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[TimesheetStatusReportFilterTypes.Date]?.endDate
          ? getDateWithoutTime(
              filters[TimesheetStatusReportFilterTypes.Date]?.endDate,
            )
          : undefined,
      };

    if (isAdmin) {
      timesheetStatusReportDetailRequest.companyIds = !filters[
        TimesheetStatusReportFilterTypes.Member
      ].includes(ExtraItemEnum.All)
        ? filters[TimesheetStatusReportFilterTypes.Member]
        : [];
    }

    const result = await dispatch(
      sendTimesheetStatusReportDetailsExcel({
        isAdmin,
        email,
        timesheetStatusReportDetailRequest,
      }),
    );

    if (sendTimesheetStatusReportDetailsExcel.fulfilled.match(result)) {
      BaseAlert.success('Timesheet status report successfully emailed.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <BaseModal
      title={'Email the report'}
      maxWidth={468}
      open
      onCancel={handleCloseModal}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <Col xs={24} md={24}>
              <FormItem
                data-id="sendReportEmailField"
                label="Email *"
                name="email"
              >
                <Input name="email" placeholder="Enter email" autoComplete="off" />
              </FormItem>
            </Col>
          </div>
          <BaseModalFooter>
            <Button
              onClick={handleCloseModal}
              data-id="closeSendReportModalBtn"
            >
              Cancel
            </Button>
            <SubmitButton type="primary" data-id="sendReportModalBtn">
              Send
            </SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
